/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, LOCALE_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ResponsiveModule } from 'ng2-responsive';
import { AppComponent } from './app.component';
import { AppState } from './app.service';
import { GlobalState } from './app.state';
import { ServicesModule } from './shared/services/services.module';
import { SharedModule } from './shared/shared.module';
import { CdkTableModule } from '@angular/cdk/table';
import { SDKBrowserModule } from './shared/sdk';
import { routing } from './app.routing';
import { DataService } from './shared/sdk/services/core/data.service';
import { AddMemberDialogComponent } from './shared/dialogs/add-member-dialog/add-member-dialog.component';
import { AddUniversalTrackerDialogComponent } from './shared/dialogs/add-universal-tracker-dialog/add-universal-tracker-dialog.component';
import { LinkImpactLevelDialogComponent } from './shared/dialogs/link-impact-level-dialog/link-impact-level-dialog.component';
import { AddNoteDialogComponent } from './shared/dialogs/add-note-dialog/add-note-dialog.component';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthInterceptor } from './shared/sdk/services/core/auth.interceptor';
import { PapaParseService } from 'ngx-papaparse';
import { LinkOrganizationDialogComponent } from './shared/dialogs/link-organization-dialog/link-organization-dialog.component';
import { DeleteConfirmDialogComponent } from './shared/dialogs/delete-confirm-dialog/delete-confirm-dialog.component';
import { ConfirmDialogComponent } from './shared/dialogs/confirm-dialog/confirm-dialog.component';
import { ViewJsonDialogComponent } from './shared/dialogs/view-json-dialog/view-json-dialog.component';
import { OKTA_CONFIG, OktaAuthModule, OktaConfig } from '@okta/okta-angular';
import { OktaAuth, OktaAuthOptions } from '@okta/okta-auth-js';
import { environment } from 'environments/environment';
import { ThemeService } from "ng2-charts";

interface Environment {
  production: boolean;
  apiUrl: string;
  disableHttpsCheck?: boolean;
  oktaIssuer?: string;
  oktaClientId?: string;
}

const envConfig = environment as Environment;

const config: OktaAuthOptions = {
  issuer: envConfig.oktaIssuer ? envConfig.oktaIssuer : 'https://dev-login.g17.eco/oauth2/aus2a1qzwisQ8fZQX5d7',
  clientId: envConfig.oktaClientId ? envConfig.oktaClientId : '0oa274y8juADtcCbj5d7',
  redirectUri: window.location.origin + '/login/callback',
  scopes: ['openid', 'profile', 'email'],
  pkce: !environment.disableHttpsCheck,
}

const oktaAuth = new OktaAuth(config);

@NgModule({
  declarations: [
    AppComponent,
    AddMemberDialogComponent,
    AddUniversalTrackerDialogComponent,
    AddNoteDialogComponent,
    LinkImpactLevelDialogComponent,
    LinkOrganizationDialogComponent,
    DeleteConfirmDialogComponent,
    ConfirmDialogComponent,
    ViewJsonDialogComponent,
  ],
  imports: [
    CdkTableModule,
    BrowserModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    OktaAuthModule,
    ServicesModule,
    ResponsiveModule,
    SharedModule.forRoot(),
    SDKBrowserModule.forRoot(),
    routing,
  ],
  entryComponents: [
    AddMemberDialogComponent,
    AddUniversalTrackerDialogComponent,
    AddNoteDialogComponent,
    LinkImpactLevelDialogComponent,
    LinkOrganizationDialogComponent,
    DeleteConfirmDialogComponent,
    ConfirmDialogComponent,
    ViewJsonDialogComponent,
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'en-GB' },
    { provide: OKTA_CONFIG, useValue: { oktaAuth } },
    {
      provide: OktaAuth,
      useFactory(config: OktaConfig) {
        return config.oktaAuth;
      },
      deps: [OKTA_CONFIG]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    AppState,
    GlobalState,
    // AuthGuardService,
    DataService,
    ThemeService,
    PapaParseService,
  ],
  bootstrap: [AppComponent]
})

export class AppModule {
  constructor(public appState: AppState) { }
}
